import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { showToast } from "../../../../../../core/common-service/CommonService";

// Encapsulates all local state and UI handlers for a single ChapterItem:
//   - inline title editing
//   - inline add-chapter editor visibility
//   - questions section show/hide toggle
//   - chapter collapse helper (DOM)
export const useChapterItem = (item, index, chapterHandlers) => {
  const { t } = useTranslation();

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [showAddEditor, setShowAddEditor] = useState(false);
  const [showQuestions, setShowQuestions] = useState(
    !item?.generated_content || item?.generated_content?.trim() === "",
  );

  useEffect(() => {
    if (!item?.generated_content || item?.generated_content?.trim() === "") {
      setShowQuestions(true);
    }
  }, [item?.generated_content]);

  const collapseSpecificChapter = useCallback((chapterIndex) => {
    try {
      const targetElement = document.getElementById(
        `flush-collapse-${chapterIndex}`,
      );
      const buttonElement = document.querySelector(
        `[name="flush-collapse-${chapterIndex}"]`,
      );

      if (targetElement && buttonElement) {
        const isExpanded =
          targetElement.classList.contains("show") ||
          buttonElement.getAttribute("aria-expanded") === "true";

        if (isExpanded) {
          if (window.bootstrap && window.bootstrap.Collapse) {
            const bsCollapse =
              window.bootstrap.Collapse.getOrCreateInstance(targetElement);
            bsCollapse.hide();
          } else if (window.$ && window.$.fn.collapse) {
            window.$(targetElement).collapse("hide");
          } else {
            targetElement.classList.remove("show");
            buttonElement.classList.add("collapsed");
            buttonElement.setAttribute("aria-expanded", "false");
          }
        }
      }
    } catch (e) {}
  }, []);

  const handleEditChapter = useCallback(
    (e) => {
      e.stopPropagation();
      collapseSpecificChapter(index);
      setTimeout(() => {
        setEditedTitle(item?.name || "");
        setIsEditingTitle(true);
      }, 100);
    },
    [collapseSpecificChapter, index, item?.name],
  );

  const handleTitleChange = useCallback((e) => {
    setEditedTitle(e.target.value);
  }, []);

  const handleTitleSubmit = useCallback(() => {
    if (!editedTitle.trim()) {
      showToast({ type: "error", message: t("please_add_chapter_title") });
      return;
    }
    chapterHandlers.onUpdateChapter({ ...item, name: editedTitle }, index);
    setIsEditingTitle(false);
  }, [editedTitle, t, chapterHandlers, item, index]);

  const handleAddChapterClick = useCallback(
    (e) => {
      e.stopPropagation();
      collapseSpecificChapter(index);
      setTimeout(() => {
        setShowAddEditor(true);
      }, 100);
    },
    [collapseSpecificChapter, index],
  );

  const handleInlineSubmit = useCallback(
    (chapterData) => {
      chapterHandlers.onAddChapter(chapterData, index);
      setShowAddEditor(false);
    },
    [chapterHandlers, index],
  );

  const handleInlineCancel = useCallback(() => {
    setShowAddEditor(false);
  }, []);

  const toggleQuestionsVisibility = useCallback((e) => {
    e.stopPropagation();
    setShowQuestions((prev) => !prev);
  }, []);

  return {
    isEditingTitle,
    editedTitle,
    showAddEditor,
    showQuestions,
    questionCount: item?.questions?.length || 0,
    handleEditChapter,
    handleTitleChange,
    handleTitleSubmit,
    handleAddChapterClick,
    handleInlineSubmit,
    handleInlineCancel,
    toggleQuestionsVisibility,
  };
};
